/**
 * Firestore persistence for dashboard layouts (Beta-1 plan P3-19, P3-11).
 *
 *   Hugo_UserPreferences/{uid}/dashboards/{orgId}
 *     { [dashboardKey]: { version, items, updatedAt }, updatedAt }
 *
 * Layouts live in a per-organization sub-document (not in the user's main preferences doc, which
 * holds private settings) so the rules can let an **org admin read a member's layouts for that
 * org** — View As (P3-11) shows the member's dashboard exactly as they arranged it — while only
 * the owner writes. One read on load (getDoc — no listener: the layout only changes through this
 * client), one write on Done. The first Beta-1 builds stored `dashboards.{orgId}.{key}` on the
 * main doc; `loadDashboardLayout` falls back to that once and the next Done moves it here.
 */
import { db } from '../../firebase';
import { doc, getDoc, setDoc, deleteField, serverTimestamp } from 'firebase/firestore';
import { serializeLayout } from './layoutStorage.js';

export const PREFS_COLLECTION = 'Hugo_UserPreferences';
export const LAYOUTS_SUBCOLLECTION = 'dashboards';

const layoutRef = (uid, orgId) => doc(db, PREFS_COLLECTION, uid, LAYOUTS_SUBCOLLECTION, orgId);

/** The stored value for one dashboard, or null. */
export async function loadDashboardLayout(uid, orgId, dashboardKey) {
  if (!uid || !orgId || !dashboardKey) return null;
  const snap = await getDoc(layoutRef(uid, orgId));
  if (snap.exists() && snap.data()?.[dashboardKey]) return snap.data()[dashboardKey];
  // Legacy location (pre P3-11): only reachable by the owner / platform admins.
  try {
    const legacy = await getDoc(doc(db, PREFS_COLLECTION, uid));
    return legacy.exists() ? legacy.data()?.dashboards?.[orgId]?.[dashboardKey] || null : null;
  } catch {
    return null;
  }
}

export async function saveDashboardLayout(uid, orgId, dashboardKey, layout) {
  if (!uid || !orgId || !dashboardKey) throw new Error('saveDashboardLayout: uid, orgId and dashboardKey are required');
  await setDoc(
    layoutRef(uid, orgId),
    { [dashboardKey]: { ...serializeLayout(layout), updatedAt: serverTimestamp() }, updatedAt: serverTimestamp() },
    { merge: true }
  );
}

/** Remove the saved layout so the role default applies again ("Reset to default"). */
export async function resetDashboardLayout(uid, orgId, dashboardKey) {
  if (!uid || !orgId || !dashboardKey) return;
  await setDoc(layoutRef(uid, orgId), { [dashboardKey]: deleteField(), updatedAt: serverTimestamp() }, { merge: true });
  // Clear the legacy copy too, or it would come back through the fallback.
  try {
    await setDoc(doc(db, PREFS_COLLECTION, uid), { dashboards: { [orgId]: { [dashboardKey]: deleteField() } } }, { merge: true });
  } catch {
    /* not the owner (View As is read-only anyway) */
  }
}
